import { useCallback, useState, useEffect } from 'react'

/**
 * Hook for tracking upload progress, handling success/failure states,
 * and managing navigation after upload completion
 *
 * @param {Object} uppy - Uppy instance to track progress for
 * @returns {Object} Hook interface containing progress state and handlers
 *
 */
const useUploadProgress = uppy => {
  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    progress: 0,
    successfulFiles: [],
    failedFiles: [],
    totalFiles: 0
  })

  const [ingestion, setIngestion] = useState({
    visible: false,
    stage: '', // 'starting' | 'complete'
    message: ''
  })

  /**
   * Invalidates gallery cache to ensure newly uploaded images appear
   */
  const invalidateGalleryCache = useCallback(() => {
    try {
      // Clear all gallery-related cache entries to force refresh
      const keysToRemove = []
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && key.includes('gallery_state')) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key))
      console.log('Gallery cache invalidated after successful upload')
    } catch (error) {
      console.error('Failed to invalidate gallery cache:', error)
    }
  }, [])

  /**
   * Handles upload progress updates
   * @param {Object} progress - Progress data from Uppy
   */
  const handleProgress = useCallback(progress => {
    setUploadProgress(prev => ({
      ...prev,
      progress: progress.percentage,
      isUploading: progress.percentage < 100
    }))
  }, [])

  /**
   * Handles upload start event
   * @param {Object} data - Upload start data from Uppy
   */
  const handleUploadStart = useCallback(data => {
    console.log('Upload started with files:', data.fileIDs)
    setUploadProgress(prev => ({
      ...prev,
      isUploading: true,
      progress: 0,
      totalFiles: data.fileIDs ? data.fileIDs.length : 0,
      successfulFiles: [],
      failedFiles: []
    }))
  }, [])

  /**
   * Handles upload success event
   * @param {Object} result - Upload success result from Uppy
   */
  const handleUploadSuccess = useCallback(result => {
    console.log('Individual file upload success:', result)
    setUploadProgress(prev => ({
      ...prev,
      successfulFiles: [...prev.successfulFiles, result]
    }))
  }, [])

  /**
   * Handles upload error event
   * @param {Object} error - Upload error data from Uppy
   */
  const handleUploadError = useCallback(error => {
    console.error('Individual file upload error:', error)
    setUploadProgress(prev => ({
      ...prev,
      failedFiles: [...prev.failedFiles, error]
    }))
  }, [])

  /**
   * Handles upload completion (success or failure)
   * @param {Object} result - Final upload result from Uppy
   */
  const handleUploadComplete = useCallback(
    result => {
      // Always log successful and failed files to match prod behavior
      console.log('successful files:', result.successful)
      console.log('failed files:', result.failed)

      setUploadProgress(prev => ({
        ...prev,
        isUploading: false,
        progress: 100,
        successfulFiles: result.successful,
        failedFiles: result.failed
      }))

      // Show completion modal every time (do NOT auto-navigate)
      setIngestion({ visible: true, stage: 'complete', message: '' })

      // Invalidate gallery cache so gallery will refresh when user visits
      invalidateGalleryCache()
      console.log(`Upload complete: ${result.successful?.length || 0} files uploaded successfully; showing completion modal`)
    },
    [invalidateGalleryCache]
  )

  /**
   * Hides the ingestion modal
   */
  const hideIngestion = useCallback(() => {
    setIngestion(prev => ({ ...prev, visible: false }))
  }, [])

  /**
   * Sets up event listeners for Uppy instance
   */
  useEffect(() => {
    if (!uppy) return

    // Set up event listeners
    uppy.on('upload', handleUploadStart)
    uppy.on('progress', handleProgress)
    uppy.on('upload-success', handleUploadSuccess)
    uppy.on('upload-error', handleUploadError)
    uppy.on('complete', handleUploadComplete)

    // Cleanup event listeners on unmount
    return () => {
      uppy.off('upload', handleUploadStart)
      uppy.off('progress', handleProgress)
      uppy.off('upload-success', handleUploadSuccess)
      uppy.off('upload-error', handleUploadError)
      uppy.off('complete', handleUploadComplete)
    }
  }, [
    uppy,
    handleUploadStart,
    handleProgress,
    handleUploadSuccess,
    handleUploadError,
    handleUploadComplete
  ])

  return {
    uploadProgress,
    ingestion,
    hideIngestion,
    // Individual handlers exposed for advanced usage
    handleProgress,
    handleUploadStart,
    handleUploadSuccess,
    handleUploadError,
    handleUploadComplete
  }
}

export default useUploadProgress
